import { ElementProps } from '@/components/sections/builder/types';
import InputSlider from '@/components/sections/builder/settings/input-slider';
import InputTab from '@/components/sections/builder/settings/input-tab';

import { useBuilderStore } from '@/stores/builder';
import { shallow } from 'zustand/shallow';
import { CollageElementDataType } from '@/components/sections/builder/elements/body/collage/type';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import SchemeSetting from '@/components/sections/builder/settings/scheme-setting';
import TextEditorMinimal from '@/components/commons/minimal-tiptap/minimal-editor';

const HEADING_SIZE_OPTIONS = [
  { value: 'small', label: 'Small' },
  { value: 'medium', label: 'Medium' },
  { value: 'large', label: 'Large' },
  { value: 'extraLarge', label: 'Extra large' },
  { value: 'extraExtraLarge', label: 'Extra extra large' },
];

const CARD_STYLE_OPTIONS = [
  { value: 'all', label: 'Style all as product cards' },
  { value: 'individual', label: 'Use individual card styles' },
];

export default function ElementSetting({ element }: ElementProps) {
  const builder = useBuilderStore(
    (state) => ({
      setElementData: state.setElementData,
      clearSelected: state.clearSelected,
    }),
    shallow
  );

  const data = element.data as CollageElementDataType;

  return (
    <div>
      <div className='mb-1 text-sm md:mt-4'>Heading</div>
      <TextEditorMinimal
        value={data.headingLabel}
        onChange={(val) => {
          builder.setElementData(element.id, { headingLabel: val });
        }}
        output='html'
        editorProps={{
          attributes: {
            class: 'prose lg:prose-lg prose-blue dark:prose-invert p-2',
          },
        }}
        editable
      />
      <div className='mb-1 text-sm md:mt-4'>Heading Size</div>
      <div>
        <Select
          name='headingSize'
          value={data.headingSize}
          onValueChange={(val) => {
            builder.setElementData(element.id, {
              headingSize: val,
            });
          }}
        >
          <SelectTrigger className='py-5'>
            <SelectValue placeholder='Select' />
          </SelectTrigger>
          <SelectContent>
            {HEADING_SIZE_OPTIONS.map((obj) => (
              <SelectItem key={obj.value} value={obj.value}>
                {obj.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className='mb-2 mt-4 text-sm'>Desktop Layout</div>
      <InputTab
        defaultValue={data.desktopLayout}
        grid={2}
        options={[
          { label: 'Large Left Block', value: 'largeLeftBlock' },
          { label: 'Large Right Block', value: 'largeRightBlock' },
        ]}
        onChange={(v) => builder.setElementData(element.id, { desktopLayout: v })}
      />
      <div className='mb-2 mt-4 text-sm'>Mobile Layout</div>
      <InputTab
        defaultValue={data.mobileLayout}
        grid={2}
        options={[
          { label: 'Collage', value: 'collage' },
          { label: 'Column', value: 'column' },
        ]}
        onChange={(v) => builder.setElementData(element.id, { mobileLayout: v })}
      />
      <div className='mb-1 text-sm md:mt-4'>Card Style</div>
      <div>
        <Select
          name='cardStyle'
          value={data.cardStyle}
          onValueChange={(val) => {
            builder.setElementData(element.id, {
              cardStyle: val,
            });
          }}
        >
          <SelectTrigger className='py-5'>
            <SelectValue placeholder='Select' />
          </SelectTrigger>
          <SelectContent>
            {CARD_STYLE_OPTIONS.map((obj) => (
              <SelectItem key={obj.value} value={obj.value}>
                {obj.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className='mb-1 text-sm md:mt-4'>Content Alignment</div>
      <InputTab
        defaultValue={data.contentAlign}
        options={[
          { label: 'Left', value: 'left' },
          { label: 'Center', value: 'center' },
          { label: 'Right', value: 'right' },
        ]}
        onChange={(v) => builder.setElementData(element.id, { contentAlign: v })}
      />

      <div className='mb-1 text-sm md:mt-4'>Color Scheme</div>
      <SchemeSetting
        value={data.colorSchemeCollageSection}
        onChange={(theme) => {
          builder.setElementData(element.id, {
            colorSchemeCollageSection: theme,
          });
        }}
      />
      <div className='mb-1 mt-4 text-base font-bold md:mt-4'>Section Padding</div>
      <InputSlider
        label='Top padding'
        value={data.topPadding}
        onChange={(val) =>
          builder.setElementData(element.id, {
            topPadding: val,
          })
        }
      />
      <InputSlider
        label='Bottom padding'
        value={data.bottomPadding}
        onChange={(val) =>
          builder.setElementData(element.id, {
            bottomPadding: val,
          })
        }
      />
    </div>
  );
}
